import { GROUP_ALLOWED_ACTIONS } from "../data/fieldTypes";
import {
  CONTEXT_TRIGGER_PREFIX,
  isContextTrigger,
  type FormLogicContext,
} from "../data/contextTriggers";
import type { FormQuestion, ConditionalLogic, FormPage } from "../types/form.types";

export interface EvalResult {
  hiddenQuestions: Set<string>;
  shownQuestions: Set<string>;
  requiredByLogic: Set<string>;
  hiddenSections: Set<string>;
  hiddenPages: Set<string>;
  jumpToPageId: string | null;
  context?: FormLogicContext;
}

export interface QuestionRulesResult {
  hiddenQuestions: Set<string>;
  shownQuestions: Set<string>;
  requiredByLogic: Set<string>;
}

export interface GroupEntryEvalResult {
  visibleFields: FormQuestion[];
  requiredIds: Set<string>;
}

const MAX_GROUP_ENTRY_LOGIC_DEPTH = 4;

function readTriggerValue(
  triggerId: string,
  answers: Record<string, any>,
  context?: FormLogicContext,
): unknown {
  if (isContextTrigger(triggerId)) return context?.[triggerId.slice(CONTEXT_TRIGGER_PREFIX.length)];
  return answers[triggerId];
}

export function evaluateCondition(
  rule: ConditionalLogic,
  answers: Record<string, any>,
  context?: FormLogicContext,
): boolean {
  const answer = readTriggerValue(rule.triggerQuestionId, answers, context);
  // An unknown context value matches no condition, negative ones included,
  // so a context-gated question stays hidden when the backend sends no context.
  if (answer === undefined && isContextTrigger(rule.triggerQuestionId)) return false;
  const answerArray = Array.isArray(answer) ? answer : answer ? [String(answer)] : [];
  const answerStr = answerArray[0] || "";
  const isEmpty =
    !answer || (Array.isArray(answer) ? answer.length === 0 : String(answer).trim() === "");

  switch (rule.condition) {
    case "is":
      return rule.triggerValues.length > 0 && answerArray.includes(rule.triggerValues[0]);
    case "is_not":
      return rule.triggerValues.length > 0 && !answerArray.includes(rule.triggerValues[0]);
    case "is_empty":
      return isEmpty;
    case "is_not_empty":
      return !isEmpty;
    case "is_any_of":
      return rule.triggerValues.some((v) => answerArray.includes(v));
    case "is_not_any_of":
      return !rule.triggerValues.some((v) => answerArray.includes(v));
    case "contains":
      return (
        rule.triggerValues.length > 0 &&
        answerStr.toLowerCase().includes(rule.triggerValues[0].toLowerCase())
      );
    case "does_not_contain":
      return (
        rule.triggerValues.length > 0 &&
        !answerStr.toLowerCase().includes(rule.triggerValues[0].toLowerCase())
      );
    case "starts_with":
      return (
        rule.triggerValues.length > 0 &&
        answerStr.toLowerCase().startsWith(rule.triggerValues[0].toLowerCase())
      );
    case "does_not_start_with":
      return (
        rule.triggerValues.length > 0 &&
        !answerStr.toLowerCase().startsWith(rule.triggerValues[0].toLowerCase())
      );
    case "ends_with":
      return (
        rule.triggerValues.length > 0 &&
        answerStr.toLowerCase().endsWith(rule.triggerValues[0].toLowerCase())
      );
    case "does_not_end_with":
      return (
        rule.triggerValues.length > 0 &&
        !answerStr.toLowerCase().endsWith(rule.triggerValues[0].toLowerCase())
      );
    case "is_before":
    case "is_after":
    case "is_on_or_before":
    case "is_on_or_after":
    case "is_between": {
      if (!answerStr || !rule.triggerValues[0]) return false;
      const normalize = (val: string): number => {
        if (val.includes("T")) return new Date(val).getTime();
        if (val.includes(":") && !val.includes("-")) {
          const [h, m] = val.split(":").map(Number);
          return h * 60 + m;
        }
        return new Date(val).getTime();
      };
      const ansVal = normalize(answerStr);
      const trigVal = normalize(rule.triggerValues[0]);
      switch (rule.condition) {
        case "is_before":
          return ansVal < trigVal;
        case "is_after":
          return ansVal > trigVal;
        case "is_on_or_before":
          return ansVal <= trigVal;
        case "is_on_or_after":
          return ansVal >= trigVal;
        case "is_between": {
          if (!rule.triggerValues[1]) return false;
          return ansVal >= trigVal && ansVal <= normalize(rule.triggerValues[1]);
        }
      }
      return false;
    }
    default:
      return false;
  }
}

export function evaluateQuestionRules(
  questions: FormQuestion[],
  answers: Record<string, any>,
  allowedActions?: ConditionalLogic["action"][],
  context?: FormLogicContext,
): QuestionRulesResult {
  const hidden = new Set<string>();
  const shown = new Set<string>();
  const required = new Set<string>();

  questions.forEach((question) => {
    if (!question.conditionalLogic || !question.conditionalLogicEnabled) return;

    question.conditionalLogic.forEach((rule) => {
      if (allowedActions && !allowedActions.includes(rule.action)) return;

      // If triggerQuestionId is not set or is the question itself, use the question's ID
      const effectiveRule = {
        ...rule,
        triggerQuestionId: rule.triggerQuestionId || question.id,
      };

      const conditionMet = evaluateCondition(effectiveRule, answers, context);

      if (conditionMet) {
        switch (rule.action) {
          case "hide":
            (rule.targetQuestionIds || []).forEach((id) => hidden.add(id));
            break;
          case "show":
            (rule.targetQuestionIds || []).forEach((id) => shown.add(id));
            break;
          case "require":
            (rule.targetQuestionIds || []).forEach((id) => required.add(id));
            break;
          default:
            break;
        }
      } else {
        // Inverse logic for "show" actions — if condition NOT met, hide the targets
        if (rule.action === "show") {
          (rule.targetQuestionIds || []).forEach((id) => hidden.add(id));
        }
      }
    });
  });

  return { hiddenQuestions: hidden, shownQuestions: shown, requiredByLogic: required };
}

export function evaluateAllLogic(
  pages: FormPage[],
  answers: Record<string, any>,
  context?: FormLogicContext,
): EvalResult {
  const hiddenSections = new Set<string>();
  const hiddenPages = new Set<string>();
  let jumpToPageId: string | null = null;

  // Collect all questions across all pages/sections
  const allQuestions: FormQuestion[] = [];
  pages.forEach((p) => p.sections.forEach((s) => s.questions.forEach((q) => allQuestions.push(q))));

  const questionRules = evaluateQuestionRules(allQuestions, answers, undefined, context);

  allQuestions.forEach((question) => {
    if (!question.conditionalLogic || !question.conditionalLogicEnabled) return;

    question.conditionalLogic.forEach((rule) => {
      // If triggerQuestionId is not set or is the question itself, use the question's ID
      const effectiveRule = {
        ...rule,
        triggerQuestionId: rule.triggerQuestionId || question.id,
      };

      const conditionMet = evaluateCondition(effectiveRule, answers, context);

      if (conditionMet) {
        switch (rule.action) {
          case "hide_section":
            (rule.targetSectionIds || []).forEach((id) => hiddenSections.add(id));
            break;
          case "show_section":
            // When condition is met and action is show_section, do nothing (section stays visible)
            break;
          case "hide_page":
            (rule.targetPageIds || []).forEach((id) => hiddenPages.add(id));
            break;
          case "show_page":
            break;
          case "jump_to_page":
            if (rule.targetPageIds && rule.targetPageIds.length > 0) {
              jumpToPageId = rule.targetPageIds[0];
            }
            break;
          default:
            break;
        }
      } else {
        if (rule.action === "show_section") {
          (rule.targetSectionIds || []).forEach((id) => hiddenSections.add(id));
        }
        if (rule.action === "show_page") {
          (rule.targetPageIds || []).forEach((id) => hiddenPages.add(id));
        }
      }
    });
  });

  return {
    hiddenQuestions: questionRules.hiddenQuestions,
    shownQuestions: questionRules.shownQuestions,
    requiredByLogic: questionRules.requiredByLogic,
    hiddenSections,
    hiddenPages,
    jumpToPageId,
    context,
  };
}

export function getPageJumpToId(
  page: FormPage,
  answers: Record<string, any>,
  context?: FormLogicContext,
): string | null {
  for (const section of page.sections) {
    for (const question of section.questions) {
      if (!question.conditionalLogic || !question.conditionalLogicEnabled) continue;
      for (const rule of question.conditionalLogic) {
        if (rule.action !== "jump_to_page") continue;
        const effectiveRule = {
          ...rule,
          triggerQuestionId: rule.triggerQuestionId || question.id,
        };
        if (evaluateCondition(effectiveRule, answers, context) && rule.targetPageIds?.length) {
          return rule.targetPageIds[0];
        }
      }
    }
  }
  return null;
}

export function isQuestionVisible(
  questionId: string,
  question: FormQuestion,
  evalResult: EvalResult,
): boolean {
  if (evalResult.hiddenQuestions.has(questionId)) return false;
  if (question.hidden && !evalResult.shownQuestions.has(questionId)) return false;
  return true;
}

export function isQuestionRequired(question: FormQuestion, evalResult: EvalResult): boolean {
  return question.required || evalResult.requiredByLogic.has(question.id);
}

function computeVisibleFieldIds(fields: FormQuestion[], rules: QuestionRulesResult): Set<string> {
  const visible = new Set<string>();
  fields.forEach((field) => {
    if (rules.hiddenQuestions.has(field.id)) return;
    if (field.hidden && !rules.shownQuestions.has(field.id)) return;
    visible.add(field.id);
  });
  return visible;
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function pickVisibleValues(
  fields: FormQuestion[],
  item: Record<string, unknown>,
  visibleIds: Set<string>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if ("_itemId" in item) result._itemId = item._itemId;
  fields.forEach((field) => {
    if (!visibleIds.has(field.id)) return;
    if (field.id in item) result[field.id] = item[field.id];
    const otherKey = `${field.id}__other`;
    if (otherKey in item) result[otherKey] = item[otherKey];
  });
  return result;
}

export function evaluateGroupEntry(
  group: FormQuestion,
  item: Record<string, unknown>,
  context?: FormLogicContext,
): GroupEntryEvalResult {
  const fields = group.fields || [];

  let workingAnswers: Record<string, unknown> = { ...item };
  let rules = evaluateQuestionRules(fields, workingAnswers, GROUP_ALLOWED_ACTIONS, context);
  let visibleIds = computeVisibleFieldIds(fields, rules);

  for (let depth = 0; depth < MAX_GROUP_ENTRY_LOGIC_DEPTH; depth++) {
    const strippedAnswers = pickVisibleValues(fields, workingAnswers, visibleIds);
    const nextRules = evaluateQuestionRules(
      fields,
      strippedAnswers,
      GROUP_ALLOWED_ACTIONS,
      context,
    );
    const nextVisibleIds = computeVisibleFieldIds(fields, nextRules);
    const converged = setsEqual(nextVisibleIds, visibleIds);

    workingAnswers = strippedAnswers;
    rules = nextRules;
    visibleIds = nextVisibleIds;

    if (converged) break;
  }

  return {
    visibleFields: fields.filter((field) => visibleIds.has(field.id)),
    requiredIds: rules.requiredByLogic,
  };
}

export function stripHiddenEntryValues(
  group: FormQuestion,
  item: Record<string, unknown>,
  context?: FormLogicContext,
): Record<string, unknown> {
  const { visibleFields } = evaluateGroupEntry(group, item, context);
  const visibleIds = new Set(visibleFields.map((field) => field.id));
  return pickVisibleValues(group.fields || [], item, visibleIds);
}
